import { Injectable } from '@angular/core';
import { I18NService } from '@ebw-monorepo/i18n-translate';
import { EbwModalService } from '@ebw-monorepo/modal';
import { UtilsService } from '@ebw-monorepo/utils';
import { API_URLS, ApiUrlService } from '../../../../utils/src/lib/services/api-url.service';
import { MatDialog } from '@angular/material/dialog';
import { ExportDialogComponent } from '../components/export-dialog/export-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class ExportService {
  constructor(
    private _ebwModalService: EbwModalService,
    private _i18nService: I18NService,
    private _utilsService: UtilsService,
    private _apiUrlService: ApiUrlService,
    private _dialog: MatDialog
  ) {}

  // ------------------------------------------------------------------------------------------------------------

  /**
   * Function to open an export dialog. The parameters are optional but there needs to be at least one
   *
   * @param {any} syncData Current data to export in case the user chooses to only export the current table
   * @param {object} asyncData Data to perform an async export
   * @param {number} asyncData.queryId ID of the query to export
   * @param {string} asyncData.queryName Name of the query to export
   */
  public openExportDialog(syncData?: any, asyncData?: { queryId: number; queryName: string }): void {
    // open a new dialog with the ExportDialogComponent and the features data
    this._dialog.open(ExportDialogComponent, { data: { syncData, asyncData } });
  }

  export(
    type: 'excel' | 'csv' | 'pdf' | 'html' | 'pdf_checklist' | 'shape',
    body: any,
    filename: string,
    headers: { title: string; data: { label: string; desc: string }[] } | {} = null,
    projection?: string
  ) {
    let exportApiUrl = '';
    let blobType = 'text/plain';

    // se non ho elementi esco
    if (!body) {
      this._ebwModalService.openInfoModal({
        title: this._i18nService.getInstantValue('map.map_table.no_data_to_export'),
        content: ''
      });
      return;
    }

    // Definisci i caratteri non consentiti nel filename
    const invalidChars = /[\/\\:*?"<>|]/g;
    // Rimpiazza i caratteri non consentiti con un trattino basso (_)
    filename = filename.replace(invalidChars, '_');

    if (type.toLowerCase() === 'excel') {
      exportApiUrl = this._apiUrlService.createUrl(API_URLS.MAP.EXPORT.EXCEL);
      blobType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      // aggiungo l'estensione al filename per il download
      filename += '.xlsx';
    }
    if (type.toLowerCase() === 'csv') {
      exportApiUrl = this._apiUrlService.createUrl(API_URLS.MAP.EXPORT.CSV);
      blobType = 'text/csv';
      // aggiungo l'estensione al filename per il download
      filename += '.csv';
    }
    if (type.toLowerCase() === 'pdf') {
      exportApiUrl = this._apiUrlService.createUrl(API_URLS.MAP.EXPORT.PDF);
      blobType = 'application/pdf';
      // aggiungo l'estensione al filename per il download
      filename += '.pdf';
    }

    if (type.toLowerCase() === 'html') {
      exportApiUrl = this._apiUrlService.createUrl(API_URLS.MAP.EXPORT.HTML);
      blobType = 'text/html';
      filename += '.html';
    }
    if (type.toLowerCase() === 'pdf_checklist') {
      exportApiUrl = this._apiUrlService.createUrl(API_URLS.MAP.EXPORT.PDF_CHECKLIST);
      blobType = 'application/pdf';
    }
    if (type.toLowerCase() === 'shape') {
      exportApiUrl = this._apiUrlService.createUrl(API_URLS.MAP.EXPORT.SHAPE);
      blobType = 'application/zip';
    }

    const url = `${exportApiUrl}?filename=${filename}&projection=${projection}`;
    const opt = { responseType: 'blob' };
    this._utilsService.postRequest(url, { data: body, headers }, false, opt, 'application/json').subscribe({
      next: (data: any) => {
        const downloadLink = document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(new Blob([data], { type: blobType }));
        downloadLink.setAttribute('download', filename);
        document.body.appendChild(downloadLink);
        downloadLink.click();
      },
      error: (err) => {
        this._ebwModalService.openErrorModal({
          title: this._i18nService.getInstantValue('common.error'),
          content: 'Errore export' // TODO mettere errore nella libreria
        });
      }
    });
  }

  /**
   * Method for exporting a whole query asynchronously
   *
   * @param {string} type Format type to export the data in
   * @param {number} queryId ID of the query to extract
   * @param {string} queryName Name of the query to extract. This will be the name of the file
   * @param {string} projection Optional EPSG code for the desired projection
   * @author Joshua Micheletti <j.micheletti@ebw.it>
   */
  public exportAsync(type: 'excel' | 'csv' | 'pdf' | 'shape' | 'html', queryId: number, queryName: string, projection?: string): void {
    // make an HTTP GET request towards the extract url passing the type, id and name (of the query) as query parameters
    this._utilsService
      .getRequest(this._apiUrlService.createUrl(API_URLS.MAP.QUERY.EXTRACT), {
        type: type,
        id: queryId,
        name: queryName,
        projection: projection
      })
      .subscribe({
        next: (response) => {
          // show an information about the query export being ready soon
          this._ebwModalService.openSuccessModal({
            title: this._i18nService.getInstantValue('map.map_table.async_export_success'),
            content: this._i18nService.getInstantValue('map.map_table.async_export_info')
          });
        },
        // if there was an error, show it to the user
        error: (error) => {
          console.error('[ExportService][exportAsync]', error);
          this._ebwModalService.openErrorModal({
            title: this._i18nService.getInstantValue('common.error'),
            content: this._i18nService.getInstantValue('map.map_table.export_error')
          });
        }
      });
  }
}
